import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicLayout from "@/components/PublicLayout";
import {
  ArrowRight, Handshake, Check, X, Palette, Receipt, Zap, Cpu,
  Sparkles, TrendingUp,
} from "lucide-react";

/**
 * Fase 14.6 · Landing B2B2C focus (aliados white-label).
 */
const PARA_QUIEN = [
  { name: "Universidades", desc: "Facultades de negocios y economía que quieran ofrecer educación continua bajo su marca." },
  { name: "Asociaciones", desc: "Colegios profesionales y asociaciones gremiales con audiencia cautiva." },
  { name: "Colegios profesionales", desc: "Contadores, actuarios, riesgo, compliance: ofrezcan formación certificada." },
  { name: "Resellers e institutos", desc: "Institutos privados y resellers verticales de capacitación." },
  { name: "Consultoras", desc: "Firmas de consultoría fintech que quieran monetizar su expertise." },
];

const INCLUYE = [
  { icon: Palette, title: "Landing builder white-label", desc: "Edita hero, secciones, imágenes y CTA sin código. Preview en vivo antes de publicar." },
  { icon: Receipt, title: "Comisiones + statements PDF", desc: "Reglas de comisión con priority + statements mensuales automatizados con marca de agua." },
  { icon: Zap, title: "Public API v1 + Webhooks", desc: "Integra ventas, usuarios y progreso con tu ERP/CRM/LMS. HMAC-signed webhooks." },
  { icon: Cpu, title: "Content Studio con IA", desc: "Crea programas propios en horas: video con nano-banana, audio, PDFs y slides." },
  { icon: Sparkles, title: "Catálogo curado", desc: "Empieza con 195+ programas DCC o publica solo los tuyos. Filtros configurables." },
  { icon: TrendingUp, title: "Reportería de partner", desc: "MRR, alumnos activos, revenue, cohortes y funnel — todo con branding propio." },
];

const CONSTRUIR_VS_ALIARSE = [
  { area: "Time-to-market", construir: "6-12 meses de desarrollo", con: "2-4 semanas al aire" },
  { area: "Costo inicial", construir: "$3-8M MXN en dev + infra", con: "$0 setup fee para partners piloto" },
  { area: "Contenido", construir: "Producir cursos desde cero", con: "195+ programas listos + Content Studio" },
  { area: "Certificados", construir: "Sistema custom + auditoría", con: "Certificados verificables incluidos" },
  { area: "Payments + facturación", construir: "Stripe integration + billing", con: "Ya integrado · MSI + suscripciones" },
  { area: "Actualizaciones regulatorias", construir: "Actualizar contenido manual", con: "Currícula actualizada por DCC" },
];

const REVENUE_MODEL = [
  { title: "50/50 default", desc: "Partner recibe 50% del revenue neto de cada venta bajo su tenant." },
  { title: "Configurable por programa", desc: "Puedes negociar splits distintos para programas de alto ticket (60/40 o 70/30)." },
  { title: "Statements mensuales", desc: "El primer día del mes recibes PDF con detalle de comisiones + pago SPEI/Stripe." },
  { title: "Sin costos ocultos", desc: "Sin fee de plataforma, sin fee por usuario, sin fee de transacción escondido." },
];

const FAQ = [
  { q: "¿Cómo funciona el revenue share?", a: "Default 50/50 sobre revenue neto (post-impuestos y costos de payment gateway). Configurable por programa. Statements mensuales con PDF y pago automatizado (mock en fase beta)." },
  { q: "¿Puedo usar mi dominio y mi email?", a: "Sí. Subdominio dcc.tumarca.com incluido, dominio propio disponible con SSL. Emails transaccionales con branding partner." },
  { q: "¿Qué contenido puedo publicar?", a: "Los 195+ programas curados por DCC, tus programas creados con Content Studio, o mix. Tú eliges el catálogo visible en tu tenant." },
  { q: "¿Hay exclusividad territorial?", a: "Casos por caso. Aliados institucionales grandes pueden negociar exclusividad regional. Programa universitario y asociación pueden coexistir por segmento distinto." },
  { q: "¿Cuánto tiempo toma lanzar?", a: "Aliados piloto lanzan en 2-4 semanas: setup del tenant (1 sem) + branding y catálogo (1 sem) + capacitación de tu equipo (2 sem)." },
];

export default function ParaAliados() {
  return (
    <PublicLayout>
      <Helmet>
        <title>Para aliados white-label · DCC</title>
        <meta name="description" content="Universidades, asociaciones y resellers: lanza tu academia de finanzas bajo tu marca con la infraestructura DCC. Revenue share, Content Studio, API pública." />
      </Helmet>

      {/* Hero */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/30 rounded-full px-3 py-1">
                <Handshake className="w-3 h-3" /> Para aliados · B2B2C
              </span>
              <h1 className="mt-6 text-4xl sm:text-5xl font-extrabold text-slate-900 font-display leading-tight">
                Lanza tu academia de <br />
                <span className="text-primary">finanzas white-label</span>
              </h1>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl">
                Usa nuestra infraestructura completa con tu marca, tu dominio y tu catálogo.
                Nosotros ponemos la tecnología, tú pones el go-to-market. Revenue share transparente.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/demo?type=partner"
                        data-testid="b2b2c-cta-demo"
                        className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-primary hover:brightness-110 text-white font-bold text-sm shadow-lg shadow-primary/30">
                  Solicita alianza <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/partners"
                        className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md border border-slate-300 text-slate-900 hover:bg-slate-50 font-semibold text-sm">
                  Casos de alianza
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <img src="/marketing/catalog-public.png" alt="Landing white-label DCC"
                    className="rounded-xl border border-slate-200 shadow-lg"
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
          </div>
        </div>
      </section>

      {/* Para quién es */}
      <Section eyebrow="Para quién" title="Aliados que ya operan con nosotros" bg="slate">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="b2b2c-para-quien">
          {PARA_QUIEN.map((c) => (
            <div key={c.name} className="p-4 bg-white border border-slate-200 rounded-xl">
              <p className="text-sm font-bold text-slate-900">{c.name}</p>
              <p className="mt-2 text-xs text-slate-500 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Lo que incluye */}
      <Section eyebrow="Lo que incluye" title="Todo lo que necesitas para operar bajo tu marca">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="b2b2c-features">
          {INCLUYE.map((f) => (
            <div key={f.title} className="p-5 bg-white border border-slate-200 rounded-xl">
              <f.icon className="w-5 h-5 text-primary" />
              <p className="mt-3 text-sm font-bold text-slate-900">{f.title}</p>
              <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Construir vs Aliarse */}
      <Section eyebrow="Construir vs aliarse" title="Sin tener que construir tu propio LMS desde cero" bg="slate">
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="b2b2c-comparison">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-xs uppercase tracking-widest text-slate-500 font-semibold">Área</th>
                <th className="text-left px-6 py-4 text-xs uppercase tracking-widest text-slate-500 font-semibold">Construir tú mismo</th>
                <th className="text-left px-6 py-4 text-xs uppercase tracking-widest text-primary font-semibold">Aliarte con DCC</th>
              </tr>
            </thead>
            <tbody>
              {CONSTRUIR_VS_ALIARSE.map((row, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-6 py-4 text-xs uppercase tracking-widest text-slate-500 font-semibold align-top">{row.area}</td>
                  <td className="px-6 py-4 text-slate-600 align-top">
                    <X className="w-4 h-4 text-red-400 inline mr-2" />
                    {row.construir}
                  </td>
                  <td className="px-6 py-4 text-slate-900 font-medium align-top">
                    <Check className="w-4 h-4 text-emerald-500 inline mr-2" />
                    {row.con}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Revenue model */}
      <Section eyebrow="Modelo de revenue" title="Split 50/50 transparente y configurable">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="b2b2c-revenue">
          {REVENUE_MODEL.map((r) => (
            <div key={r.title} className="p-5 bg-white border-2 border-slate-200 rounded-xl">
              <p className="text-sm font-bold text-primary">{r.title}</p>
              <p className="mt-2 text-xs text-slate-500 leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section eyebrow="Preguntas frecuentes" title="Antes de firmar alianza" bg="slate">
        <div className="max-w-3xl mx-auto space-y-3" data-testid="b2b2c-faq">
          {FAQ.map((f, i) => (
            <details key={i} className="p-5 bg-white border border-slate-200 rounded-xl group">
              <summary className="text-sm font-bold text-slate-900 cursor-pointer flex items-center justify-between">
                {f.q}
                <span className="text-primary group-open:rotate-45 transition-transform text-xl">+</span>
              </summary>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </Section>

      {/* CTA final */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="p-10 md:p-14 rounded-2xl bg-gradient-to-br from-brand-orange-700 to-brand-orange-900 text-white text-center">
          <Handshake className="w-10 h-10 mx-auto text-white" />
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold font-display">
            Conversemos sobre tu academia white-label
          </h2>
          <p className="mt-3 text-base text-white/80 max-w-xl mx-auto">
            30 minutos con nuestro equipo comercial. Sin compromiso, sin fee de setup, sin exclusividad forzada.
          </p>
          <div className="mt-6">
            <Link to="/demo?type=partner"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-md bg-white text-primary font-bold text-base hover:bg-brand-orange-50 transition-all shadow-lg">
              Solicita alianza <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function Section({ eyebrow, title, children, bg }) {
  const bgClass = bg === "slate" ? "bg-slate-50" : "bg-white";
  return (
    <section className={`${bgClass} border-t border-slate-100`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs uppercase tracking-widest text-primary font-bold">{eyebrow}</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-slate-900 font-display leading-tight">{title}</h2>
        </div>
        {children}
      </div>
    </section>
  );
}
